/**
 * InvoicesListPage (11F.7) — the admin Invoices list on the 11E.3
 * EntityTable framework.
 *
 * Replaces the tab-and-modal `InvoicesPage` as the `/invoices` route. That
 * page fetched an unpaginated slice, had no search, and showed no student
 * name — the three findings the 2026-08-09/10 review raised. All three were
 * one missing capability: a server-side paged list contract, added in 11F.7.
 *
 * What lives where:
 *   - columns/filters/sorting  → `config/invoicesEntityConfig.tsx`
 *   - summary cards            → `components/InvoicesSummaryCards.tsx`
 *   - per-invoice actions      → the detail page's Actions menu, NOT row
 *     actions. Voiding needs a typed reason and is irreversible, so it
 *     belongs behind the fuller context of the detail view rather than a
 *     hover menu on a list row.
 *
 * Creating invoices still routes through the existing `InvoicesPage` modal
 * (mounted at `/invoices/new-legacy`) — rebuilding the create form was not
 * in 11F.7's scope, and it works. See the module doc for the follow-up.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import { useTenantAwareEntityList } from '@/components/entity/useTenantAwareEntityList';
import { invoicesTableConfig } from '@features/invoices/config/invoicesEntityConfig';
import { InvoicesSummaryCards } from '@features/invoices/components/InvoicesSummaryCards';
import {
  useInvoicesSummary,
  type InvoiceResponse,
} from '@features/invoices/api/billing';
import type { EntityActionKey } from '@/components/entity/types';

const PAGE_SIZE = 20;

export const InvoicesListPage: React.FC = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(invoicesTableConfig.defaultSort ?? 'issue_date');
  const [order, setOrder] = useState<'asc' | 'desc'>(
    invoicesTableConfig.defaultOrder ?? 'desc',
  );
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    invoicesTableConfig.defaultFilters ?? {},
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const queryParams = {
    status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const {
    data: result,
    isLoading,
    error,
    refetch,
    isAllMode,
    focusRowTenant,
  } = useTenantAwareEntityList<InvoiceResponse>({
    queryKey: ['invoices', 'list', queryParams],
    apiPath: invoicesTableConfig.apiPath,
    params: queryParams as unknown as Record<
      string,
      string | number | boolean | undefined | null
    >,
  });

  // Summary is scoped to the same tenant as the table. In All mode the
  // endpoint has no consolidated form (D9 forbids cross-tenant reads), so
  // the cards are suppressed rather than showing one tenant's figures above
  // a merged multi-tenant table.
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useInvoicesSummary(undefined, undefined, !isAllMode);

  const handleSortChange = (newSort: string, newOrder: 'asc' | 'desc') => {
    setSort(newSort);
    setOrder(newOrder);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const openInvoice = (invoice: InvoiceResponse) => {
    focusRowTenant(invoice);
    navigate(`/invoices/${invoice.id}`);
  };

  const handleRowAction = (action: EntityActionKey, invoice: InvoiceResponse) => {
    if (action === 'view') openInvoice(invoice);
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading
    ? 'Loading…'
    : `${total.toLocaleString('en-IN')} invoice${total === 1 ? '' : 's'}`;

  return (
    <PageContainer
      title="Invoices"
      subtitle={subtitle}
      data-testid="candeza-invoices-list-page"
    >
      {!isAllMode && (
        <InvoicesSummaryCards
          data={summary}
          isLoading={summaryLoading}
          isError={summaryError}
        />
      )}

      <Card padding="lg">
        <EntityTable
          config={invoicesTableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={openInvoice}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
          isAllMode={isAllMode}
        />
      </Card>
    </PageContainer>
  );
};

export default InvoicesListPage;
